// Single source of truth for the session.
//
// The API hands out a short-lived access JWT (returned in the body) and a refresh
// token (an httpOnly cookie scoped to /auth that scripts can't read). The access
// token is kept in localStorage plus the `authToken` cookie; when it expires, or a
// request comes back 401, `refreshSession` trades the cookie for a new one.

import { jwtDecode } from 'jwt-decode';

const TOKEN_KEY = 'authToken';
const apiBase = () => process.env.REACT_APP_API_BASE_URL;
// Access tokens are short-lived (minutes), so the cookie only needs to outlive the JWT.
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24;

const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift() || null;
    return null;
};

// Defensive: if the API ever serves the token as a JSON string (quoted) instead of
// text/plain, strip the surrounding quotes so the Bearer value stays well-formed.
const normalize = (token) =>
    typeof token === 'string' ? token.trim().replace(/^"(.*)"$/, '$1') : token;

const readStored = () => {
    try {
        const stored = window.localStorage.getItem(TOKEN_KEY);
        if (stored) return normalize(stored);
    } catch { /* localStorage unavailable (e.g. private mode) — fall back to cookie */ }
    const cookie = getCookie(TOKEN_KEY);
    return cookie ? normalize(cookie) : null;
};

export const decodeToken = (token) => {
    try {
        return token ? jwtDecode(token) : null;
    } catch {
        return null;
    }
};

// exp is seconds since epoch. Treat a missing/undecodable exp as expired so a
// malformed token can never look "valid".
export const isTokenExpired = (token) => {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    return decoded.exp * 1000 <= Date.now();
};

// Milliseconds until the stored token expires (0 when there is none or it's expired).
export const tokenTimeLeft = () => {
    const decoded = decodeToken(readStored());
    if (!decoded || !decoded.exp) return 0;
    return Math.max(0, decoded.exp * 1000 - Date.now());
};

export const clearToken = () => {
    try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    // Match the attributes used at write time so the browser actually drops it.
    document.cookie = `${TOKEN_KEY}=; path=/; secure; samesite=strict; max-age=0`;
    document.cookie = `${TOKEN_KEY}=; path=/; max-age=0`;
};

// Returns the token only if present AND unexpired; clears it (and returns null)
// once expired. This is what request auth uses, so we never send a dead token.
export const getToken = () => {
    const token = readStored();
    if (!token) return null;
    if (isTokenExpired(token)) {
        clearToken();
        return null;
    }
    return token;
};

// The stored token regardless of expiry — used to distinguish "never logged in"
// from "was logged in but the token just expired" (so we can prompt a re-login).
export const peekToken = () => readStored();

export const getEmail = () => decodeToken(readStored())?.sub || null;

// Role claims come out of the API as either "role" or the long .NET claim URI, as a
// string for one role or an array for several.
const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
export const getRoles = () => {
    const decoded = decodeToken(readStored());
    const roles = decoded?.role ?? decoded?.roles ?? decoded?.[ROLE_CLAIM] ?? [];
    return Array.isArray(roles) ? roles : [roles];
};

export const setToken = (rawToken) => {
    const token = normalize(rawToken);
    try { window.localStorage.setItem(TOKEN_KEY, token); } catch { /* ignore */ }
    document.cookie = `${TOKEN_KEY}=${token}; path=/; secure; samesite=strict; max-age=${COOKIE_MAX_AGE_SECONDS}`;
    return token;
};

// Merge an Authorization: Bearer header onto an existing headers object when a
// valid token is present. No-op when logged out or expired.
export const authHeaders = (base = {}) => {
    const token = getToken();
    return token ? { ...base, Authorization: `Bearer ${token}` } : base;
};

// Trades the refresh cookie for a new access token. Resolves to the token, or null
// when there is no live session (the stored token is cleared in that case).
// Concurrent callers share one in-flight request.
let refreshInFlight = null;
export const refreshSession = () => {
    if (!refreshInFlight) {
        refreshInFlight = fetch(`${apiBase()}/auth/refresh`, { method: 'POST', credentials: 'include' })
            .then(async (res) => {
                if (res.ok) return setToken(await res.text());
                clearToken();
                return null;
            })
            .catch(() => null)
            .finally(() => { refreshInFlight = null; });
    }
    return refreshInFlight;
};

// fetch() for authenticated calls: attaches the Bearer header, refreshes an expired
// session first, and on a 401 refreshes once and retries before giving up.
export const authFetch = async (url, options = {}) => {
    if (!getToken()) {
        await refreshSession();
    }
    const send = () => fetch(url, { credentials: 'include', ...options, headers: authHeaders(options.headers || {}) });

    const response = await send();
    if (response.status !== 401) return response;

    const refreshed = await refreshSession();
    if (!refreshed) {
        notifyUnauthorized();
        return response;
    }
    const retried = await send();
    if (retried.status === 401) notifyUnauthorized();
    return retried;
};

// Ends the session on the server (revokes the refresh token) and locally.
export const logoutSession = async () => {
    try {
        await fetch(`${apiBase()}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch { /* the local session is cleared regardless */ }
    clearToken();
};

// Called when a request is still 401 after a refresh attempt. Clears the dead
// session and fires a global event so AuthContext can drop to logged-out and
// prompt a re-login, instead of leaving the user staring at failing calls.
export const notifyUnauthorized = () => {
    clearToken();
    try { window.dispatchEvent(new CustomEvent('auth:unauthorized')); } catch { /* SSR/no window */ }
};
